import React, { useState, useEffect, useLayoutEffect, useRef } from "react";
import ReactMarkdown from "react-markdown";
import remarkGfm from "remark-gfm";
import axios from "axios";
import { Panel, PanelGroup, PanelResizeHandle } from "react-resizable-panels";
import { useAuth } from "../../../contexts/AuthContext";
import { useChatArtifact } from "../../../contexts/ChatArtifactContext";
import { cn } from "../../../lib/utils";
import { Button } from "../../ui/button";
import ChatControls from "../../ui/ChatControls";
import { Send } from "lucide-react";
import ErrorModal from "../../modals/error-modal/ErrorModal";
import ChatPdfDownloadCard from "./ChatPdfDownloadCard";
import ChatArtifactPane from "./ChatArtifactPane";
import ChatZipDownloadCard from "./ChatZipDownloadCard";
import ChatInteractionCard from "./ChatInteractionCard";
import ChatAssetBundleCard from "./ChatAssetBundleCard";
import { readFilesAsAssetEntries } from "../../../lib/assetBundleZip";

function isPdfAttachment(value) {
  return (
    value?.type === "pdf" &&
    typeof value?.data === "string" &&
    value.data.length > 0
  );
}

function isZipAttachment(value) {
  return (
    value?.type === "zip" &&
    typeof value?.data === "string" &&
    value.data.length > 0
  );
}

function zipBlobAttachmentFromMessage(msg) {
  if (isZipAttachment(msg?.zipAttachment)) return msg.zipAttachment;
  if (isZipAttachment(msg?.attachment)) return msg.attachment;
  return null;
}

function isChatInteraction(value) {
  return (
    (value?.type === "multi_select" || value?.type === "choice_buttons") &&
    Array.isArray(value?.options) &&
    value.options.length > 0
  );
}

function isAssetBundle(value) {
  return (
    value?.type === "asset_bundle" &&
    Array.isArray(value?.assets) &&
    value.assets.length > 0
  );
}

function isSameArtifact(active, next) {
  if (!active || !next || active.kind !== next.kind) return false;
  if (active.kind === "zipBundle") return active.assetBundle === next.assetBundle;
  return active.attachment === next.attachment;
}

function toggleArtifact(setter, next) {
  setter((prev) => (isSameArtifact(prev, next) ? null : next));
}

function useMinWidthMediaQuery(minWidthPx) {
  const query = `(min-width: ${minWidthPx}px)`;
  const [matches, setMatches] = useState(() =>
    typeof window !== "undefined" ? window.matchMedia(query).matches : true
  );

  useEffect(() => {
    const mq = window.matchMedia(query);
    const onChange = () => setMatches(mq.matches);
    onChange();
    mq.addEventListener("change", onChange);
    return () => mq.removeEventListener("change", onChange);
  }, [query]);

  return matches;
}

const HYDRATION_CHANNEL_INTERACTION = {
  type: "multi_select",
  id: "hydration_flask_launch_channels",
  submitLabel: "Continue",
  minSelected: 1,
  responsePrefix: "Selected launch channels",
  options: [
    { id: "google_ads", label: "Google Ads (Search + YouTube)" },
    { id: "meta", label: "Meta (Facebook + Instagram)" },
    { id: "linkedin_ads", label: "LinkedIn Ads" },
    { id: "tiktok_ads", label: "TikTok Ads" },
    { id: "email", label: "Email (customers + prospects)" },
    { id: "something_else", label: "Something else" },
  ],
};

const HYDRATION_ASSET_INTERACTION = {
  type: "choice_buttons",
  id: "hydration_flask_launch_assets",
  responsePrefix: "Product media source",
  options: [
    {
      id: "upload_files",
      label: "Upload Files",
      responseText: "Uploaded product demo video.",
    },
    {
      id: "use_stock_footage",
      label: "Use Stock Footage",
      responseText: "Use stock footage.",
    },
  ],
};

const LOAN_PIPELINE_INTRO_INTERACTION = {
  type: "choice_buttons",
  id: "loan_pipeline_forecast_intro",
  responsePrefix: "Loan pipeline forecast choice",
  options: [
    {
      id: "show_model",
      label: "Yes, Show Me",
      responseText: "Yes, show me the forecast model.",
    },
    {
      id: "pdf_only",
      label: "No, Just Give Me PDF",
      responseText: "No, just give me the PDF.",
    },
  ],
};

const LOAN_PIPELINE_MODEL_INTERACTION = {
  type: "choice_buttons",
  id: "loan_pipeline_forecast_model_options",
  responsePrefix: "Loan pipeline forecast action",
  options: [
    {
      id: "show_details",
      label: "Yes, Show Details",
      responseText: "Yes, show details.",
    },
    {
      id: "download_excel",
      label: "Download Forecast Model (CSV)",
      responseText: "Download forecast model CSV.",
    },
    {
      id: "download_pdf",
      label: "Download Full Report (PDF)",
      responseText: "Download full report PDF.",
    },
  ],
};

const LOAN_PIPELINE_DOWNLOAD_INTERACTION = {
  type: "choice_buttons",
  id: "loan_pipeline_forecast_downloads",
  responsePrefix: "Loan pipeline forecast download",
  options: [
    {
      id: "download_excel",
      label: "Download Forecast Model (CSV)",
      responseText: "Download forecast model CSV.",
    },
    {
      id: "download_pdf",
      label: "Download Full Report (PDF)",
      responseText: "Download full report PDF.",
    },
  ],
};

const VIBE_CONFIRM_INTERACTION = {
  type: "choice_buttons",
  id: "vibe_prospecting_confirm",
  responsePrefix: "Vibe contact details",
  options: [
    {
      id: "get_details",
      label: "Get full contact details",
      responseText: "Yes, please retrieve the full contact details including phone, email, and LinkedIn.",
    },
    {
      id: "no_thanks",
      label: "No thanks",
      responseText: "No thanks, I don't need the full contact details.",
    },
  ],
};

function inferChatInteraction(messageText) {
  const text = String(messageText || "");
  if (/Which channels do you want to launch on\?/i.test(text)) {
    return HYDRATION_CHANNEL_INTERACTION;
  }
  if (/Do you have product photos or videos I can use\?/i.test(text)) {
    return HYDRATION_ASSET_INTERACTION;
  }
  if (/Want me to show you the forecast model\?/i.test(text)) {
    return LOAN_PIPELINE_INTRO_INTERACTION;
  }
  if (/Want to see the month-by-month data\?/i.test(text)) {
    return LOAN_PIPELINE_MODEL_INTERACTION;
  }
  if (/\[DOWNLOAD FORECAST MODEL \((?:CSV|EXCEL)\)\]/i.test(text)) {
    return LOAN_PIPELINE_DOWNLOAD_INTERACTION;
  }
  // Vibe Prospecting confirmation — triggered by the [VIBE_CONFIRM] sentinel in the assistant message
  if (/\[VIBE_CONFIRM\]/i.test(text)) {
    return VIBE_CONFIRM_INTERACTION;
  }
  return null;
}

function displayTextWithoutInteractionControls(messageText, interaction) {
  if (!interaction) return messageText;
  let text = String(messageText || "");
  if (interaction.id === "hydration_flask_launch_channels") {
    text = text
      .replace(/^\s*[-*]\s+\[[ x]\]\s+.*$/gim, "")
      .replace(/^\s*\[CONTINUE\]\s*$/gim, "");
  }
  if (interaction.id === "hydration_flask_launch_assets") {
    text = text.replace(/\[UPLOAD FILES\]\s*\[USE STOCK FOOTAGE\]/gi, "");
  }
  if (interaction.id === "loan_pipeline_forecast_intro") {
    text = text.replace(/\[YES, SHOW ME\]\s*\[NO, JUST GIVE ME PDF\]/gi, "");
  }
  if (interaction.id === "loan_pipeline_forecast_model_options") {
    text = text
      .replace(/^\s*\[YES, SHOW DETAILS\]\s*$/gim, "")
      .replace(/^\s*\[DOWNLOAD FORECAST MODEL \((?:CSV|EXCEL)\)\]\s*$/gim, "")
      .replace(/^\s*\[DOWNLOAD FULL REPORT \(PDF\)\]\s*$/gim, "");
  }
  if (interaction.id === "loan_pipeline_forecast_downloads") {
    text = text
      .replace(/^\s*\[DOWNLOAD FORECAST MODEL \((?:CSV|EXCEL)\)\]\s*$/gim, "")
      .replace(/^\s*\[DOWNLOAD FULL REPORT \(PDF\)\]\s*$/gim, "");
  }
  if (interaction.id === "vibe_prospecting_confirm") {
    // Remove the sentinel token from displayed text; the buttons replace it
    text = text.replace(/\[VIBE_CONFIRM\]/gi, "").trim();
  }
  return text.replace(/\n{3,}/g, "\n\n").trim();
}

function buildInteractionResponseText(interaction, selectedOptions) {
  if (selectedOptions?.length === 1 && selectedOptions[0]?.responseText) {
    return selectedOptions[0].responseText;
  }
  const labels = (selectedOptions || [])
    .map((option) => option?.label)
    .filter(Boolean);
  const prefix = interaction?.responsePrefix || "Selected options";
  return `${prefix}: ${labels.join(", ")}.`;
}

async function resolvePdfAttachment(apiBase, responseData) {
  if (isPdfAttachment(responseData?.attachment)) {
    return responseData.attachment;
  }

  const payload = responseData?.pdfPayload;
  if (
    payload &&
    typeof payload.title === "string" &&
    Array.isArray(payload.sections) &&
    payload.sections.length > 0
  ) {
    try {
      const res = await axios.post(`${apiBase}/chat/generate-pdf`, {
        title: payload.title,
        sections: payload.sections,
      });
      return isPdfAttachment(res.data?.attachment) ? res.data.attachment : null;
    } catch (err) {
      console.error("PDF generation fallback failed:", err);
    }
  }

  return null;
}

export default function ChatView() {
  const { user } = useAuth();
  const { setArtifactPanelOpen } = useChatArtifact();
  const [messages, setMessages] = useState([]);
  const [input, setInput] = useState("");
  const [loadingHistory, setLoadingHistory] = useState(true);
  const [sending, setSending] = useState(false);
  const [isDragOver, setIsDragOver] = useState(false);
  const [showCreditModal, setShowCreditModal] = useState(false);
  const [activeArtifact, setActiveArtifact] = useState(null);
  const isMdUp = useMinWidthMediaQuery(768);

  const containerRef = useRef(null);
  const chatControlsRef = useRef(null);
  const uploadedScenarioAssetsRef = useRef([]);
  const savedScrollTopRef = useRef(0);

  const conversationId = user?.userId;

  const scrollToBottom = () => {
    if (containerRef.current) {
      containerRef.current.scrollTop = containerRef.current.scrollHeight;
    }
  };

  useEffect(() => {
    scrollToBottom();
  }, [messages, sending]);

  useEffect(() => {
    const el = containerRef.current;
    if (!el) return undefined;

    const saveScroll = () => {
      savedScrollTopRef.current = el.scrollTop;
    };

    el.addEventListener("scroll", saveScroll, { passive: true });
    return () => el.removeEventListener("scroll", saveScroll);
  }, []);

  // ────────────────────────────────────────────────────────────────────────────
  // History fetch
  useEffect(() => {
    const fetchHistory = async () => {
      try {
        const base = process.env.REACT_APP_API_URL.replace(/\/+$/, "");
        const endpoint = `${base}/chat/history/${conversationId}`;
        const res = await axios.get(endpoint);
        const history = res.data?.messages || [];

        // Map history to ChatView message shape (attachment restored server-side for PDF exports)
        let formatted = history.map((m) => ({
          text: m.content || "",
          isUser: m.role === "user",
          ...(m.pdfFileName ? { pdfFileName: m.pdfFileName } : {}),
          ...(m.role === "assistant" && isAssetBundle(m.assetBundle)
            ? { assetBundle: m.assetBundle }
            : {}),
          ...(m.role === "assistant"
            ? { interaction: isChatInteraction(m.interaction) ? m.interaction : inferChatInteraction(m.content) }
            : {}),
          ...(m.role === "assistant" &&
          (isPdfAttachment(m.attachment) || isZipAttachment(m.attachment))
            ? { attachment: m.attachment }
            : {}),
          ...(m.role === "assistant" && isZipAttachment(m.zipAttachment)
            ? { zipAttachment: m.zipAttachment }
            : {}),
        }));

        // If no history, start with welcome message
        if (formatted.length === 0) {
          formatted = [
            {
              text: "Hi! Ask me anything about your marketing performance.",
              isUser: false,
            },
          ];
        }

        setMessages(formatted);
        setActiveArtifact(null);
      } catch (err) {
        console.error("Failed to fetch chat history:", err);
        setMessages([
          {
            text: "Hi! Ask me anything about your marketing performance.",
            isUser: false,
          },
        ]);
        setActiveArtifact(null);
      } finally {
        setLoadingHistory(false);
      }
    };

    // Only attempt fetch when we have a user object (may be null if not authenticated)
    if (user !== undefined) {
      fetchHistory();
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [user]);

  // Handler for drag-and-drop CSV uploads (delegates to ChatControls)
  const handleDrop = async (e) => {
    e.preventDefault();
    setIsDragOver(false);
    const file = e.dataTransfer.files?.[0];
    if (!file) return;
    await chatControlsRef.current?.processCsvFile(file);
  };

  // ────────────────────────────────────────────────────────────────────────────
  const sendMessage = async (
    messageText,
    { displayText = messageText, includeCsvRecipients = false, clearComposer = false } = {}
  ) => {
    const trimmedMessage = String(messageText || "").trim();
    if (!trimmedMessage || sending || loadingHistory) return;

    const csvEmails = includeCsvRecipients
      ? chatControlsRef.current?.getCsvEmails() ?? []
      : [];
    let messageToSend = trimmedMessage;
    if (csvEmails.length) {
      messageToSend = `Recipients: ${csvEmails.join(", ")}\n\n${trimmedMessage}`;
    }

    const userMessage = { text: String(displayText || trimmedMessage).trim(), isUser: true };
    setMessages((prev) => [...prev, userMessage]);
    if (clearComposer) setInput("");
    setSending(true);

    try {
      const base = process.env.REACT_APP_API_URL.replace(/\/+$/, "");
      const endpoint = `${base}/chat/supervisor`;
      const userTimeZone = Intl.DateTimeFormat().resolvedOptions().timeZone;
      const res = await axios.post(endpoint, {
        message: messageToSend,
        userId: conversationId,
        conversationId,
        userTimeZone,
      });

      if (res?.data === false) {
        setShowCreditModal(true);
        return; // do not append agent reply
      }

      const agentReply =
        res.data?.response ||
        "Something went wrong. Please try another prompt, or contact support if this issue persists.";
      const resolvedPdf = await resolvePdfAttachment(base, res.data);
      const rawAttachment = res.data?.attachment;
      const zipFromBody = isZipAttachment(rawAttachment) ? rawAttachment : null;
      const pdfFromBody = isPdfAttachment(rawAttachment) ? rawAttachment : null;
      const pdfAtt = resolvedPdf || pdfFromBody || null;
      const attachment = pdfAtt ?? zipFromBody ?? undefined;
      const zipAttachment = pdfAtt && zipFromBody ? zipFromBody : undefined;

      const uploadedAssets = isAssetBundle(res.data?.assetBundle)
        ? uploadedScenarioAssetsRef.current
        : [];

      const assistantMsg = {
        text: agentReply,
        isUser: false,
        ...(res.data?.pdfFileName ? { pdfFileName: res.data.pdfFileName } : {}),
        ...(isAssetBundle(res.data?.assetBundle) ? { assetBundle: res.data.assetBundle } : {}),
        ...(uploadedAssets.length ? { uploadedAssets } : {}),
        ...(isChatInteraction(res.data?.interaction)
          ? { interaction: res.data.interaction }
          : { interaction: inferChatInteraction(agentReply) }),
        ...(attachment ? { attachment } : {}),
        ...(zipAttachment ? { zipAttachment } : {}),
      };

      setMessages((prev) => [...prev, assistantMsg]);

      if (pdfAtt) {
        setActiveArtifact({ kind: "pdf", attachment: pdfAtt });
      } else if (zipFromBody && !pdfAtt) {
        setActiveArtifact({ kind: "zipBlob", attachment: zipFromBody });
      }

      if (isAssetBundle(res.data?.assetBundle)) {
        uploadedScenarioAssetsRef.current = [];
      }

      // Clear CSV file info after a successful send
      chatControlsRef.current?.clearCsvSelection();
    } catch (err) {
      console.error("Chat error:", err);
      setMessages((prev) => [
        ...prev,
        {
          text: "Something went wrong. Please try another prompt, or contact support if this issue persists.",
          isUser: false,
        },
      ]);
    } finally {
      setSending(false);
    }
  };

  const handleSend = async () => {
    await sendMessage(input, {
      includeCsvRecipients: true,
      clearComposer: true,
    });
  };

  const handleInteractionSubmit = async (interaction, selectedOptions, files = []) => {
    if (files.length) {
      try {
        uploadedScenarioAssetsRef.current = await readFilesAsAssetEntries(files);
      } catch (err) {
        console.error("Failed to read uploaded campaign assets:", err);
        uploadedScenarioAssetsRef.current = [];
      }
    } else if (interaction?.id === "hydration_flask_launch_assets") {
      uploadedScenarioAssetsRef.current = [];
    }

    const responseText = files.length
      ? `Uploaded product files: ${files.map((file) => file.name).join(", ")}.`
      : buildInteractionResponseText(interaction, selectedOptions);
    await sendMessage(responseText, { displayText: responseText });
  };

  const showArtifactPanel = Boolean(activeArtifact);
  const activeInteractionIndex =
    messages.length > 0 &&
    !messages[messages.length - 1]?.isUser &&
    isChatInteraction(messages[messages.length - 1]?.interaction)
      ? messages.length - 1
      : -1;

  useLayoutEffect(() => {
    const el = containerRef.current;
    if (!el) return;
    el.scrollTop = savedScrollTopRef.current;
  }, [isMdUp, showArtifactPanel]);

  useEffect(() => {
    let id;
    const tick = () => {
      if (containerRef.current) {
        containerRef.current.scrollTop = containerRef.current.scrollHeight;
      }
    };
    id = requestAnimationFrame(() => {
      requestAnimationFrame(tick);
    });
    return () => cancelAnimationFrame(id);
  }, [showArtifactPanel, activeArtifact]);

  useEffect(() => {
    setArtifactPanelOpen(showArtifactPanel);
  }, [showArtifactPanel, setArtifactPanelOpen]);

  useEffect(() => () => setArtifactPanelOpen(false), [setArtifactPanelOpen]);

  const chatColumn = (
    <div
      className={cn(
        "flex h-full min-h-0 min-w-0 flex-col overflow-hidden pb-1 sm:pb-2",
        showArtifactPanel && isMdUp ? "pl-3 pr-2 pt-1 sm:pl-4" : ""
      )}
    >
      <ErrorModal
        open={showCreditModal}
        onClose={() => setShowCreditModal(false)}
        title="Insufficient credits"
        message="You don't have enough credits to send this chat message."
        primaryAction={{
          label: "Buy credits",
          onClick: () => {
            setShowCreditModal(false);
            window.location.href = "/plans";
          }
        }}
      />
      {/* Chat messages */}
      <div
        className="chat-scrollbar min-h-0 flex-1 overflow-y-auto overscroll-contain pb-3"
        ref={containerRef}
      >
        {loadingHistory && (
          <div className="text-muted-foreground italic">Loading chat...</div>
        )}
        {messages.map((msg, i) => {
          const interaction = isChatInteraction(msg.interaction) ? msg.interaction : null;
          const markdownText = displayTextWithoutInteractionControls(msg.text, interaction);
          const zipBlobAtt = zipBlobAttachmentFromMessage(msg);

          return (
            <div
              key={i}
              className={cn(
                "mb-4 rounded-md p-3",
                msg.isUser
                  ? cn(
                      "ml-auto bg-primary text-primary-foreground whitespace-pre-wrap break-words",
                      showArtifactPanel ? "max-w-[min(28rem,85%)]" : "max-w-[70%]"
                    )
                  : cn(
                      "mr-auto bg-accent text-accent-foreground",
                      showArtifactPanel ? "max-w-[min(42rem,92%)]" : "max-w-[70%]"
                    )
              )}
            >
              {msg.isUser ? (
                msg.text
              ) : (
                <div className="flex flex-col gap-3">
                  {markdownText ? (
                    <ReactMarkdown
                      remarkPlugins={[remarkGfm]}
                      components={{
                        a: ({ children, ...props }) => (
                          <a {...props} target="_blank" rel="noopener noreferrer">
                            {children}
                          </a>
                        ),
                        table: (props) => (
                          <div className="overflow-x-auto"><table {...props} /></div>
                        ),
                        code: ({ inline, className, children, ...props }) => (
                          <code
                            className={cn(
                              "rounded bg-background/60 px-1 py-0.5 text-xs",
                              className
                            )}
                            {...props}
                          >
                            {children}
                          </code>
                        )
                      }}
                    >
                      {markdownText}
                    </ReactMarkdown>
                  ) : null}
                  {interaction ? (
                    <ChatInteractionCard
                      interaction={interaction}
                      disabled={sending || i !== activeInteractionIndex}
                      onSubmit={handleInteractionSubmit}
                    />
                  ) : null}
                  {isAssetBundle(msg.assetBundle) ? (
                    <ChatAssetBundleCard
                      assetBundle={msg.assetBundle}
                      attachment={msg.attachment}
                      uploadedAssets={msg.uploadedAssets}
                      highlightOpen={
                        activeArtifact?.kind === "zipBundle" &&
                        activeArtifact.assetBundle === msg.assetBundle
                      }
                      onOpenPreview={() =>
                        toggleArtifact(setActiveArtifact, {
                          kind: "zipBundle",
                          assetBundle: msg.assetBundle,
                          uploadedAssets: msg.uploadedAssets,
                          pdfAttachment: isPdfAttachment(msg.attachment) ? msg.attachment : undefined,
                        })
                      }
                    />
                  ) : null}
                  {isPdfAttachment(msg.attachment) ? (
                    <ChatPdfDownloadCard
                      attachment={msg.attachment}
                      messageText={msg.text}
                      pdfFileName={msg.pdfFileName}
                      highlightOpen={
                        activeArtifact?.kind === "pdf" &&
                        activeArtifact.attachment === msg.attachment
                      }
                      onOpenPanel={() =>
                        toggleArtifact(setActiveArtifact, { kind: "pdf", attachment: msg.attachment })
                      }
                    />
                  ) : null}
                  {zipBlobAtt ? (
                    <ChatZipDownloadCard
                      attachment={zipBlobAtt}
                      messageText={msg.text}
                      highlightOpen={
                        activeArtifact?.kind === "zipBlob" && activeArtifact.attachment === zipBlobAtt
                      }
                      onOpenPanel={() =>
                        toggleArtifact(setActiveArtifact, { kind: "zipBlob", attachment: zipBlobAtt })
                      }
                    />
                  ) : null}
                </div>
              )}
            </div>
          );
        })}
        {sending && (
          <div className="text-muted-foreground italic">Mark is typing...</div>
        )}
      </div>

      {/* Input locked to bottom of chat panel; only messages scroll above */}
      <div
        className={cn(
          "relative shrink-0 flex flex-col gap-2 rounded-xl border border-border bg-background pb-2 dark:border-[hsl(240,3.7%,15%)] dark:bg-[hsl(240,3.7%,7%)] shadow-sm transition-colors",
          showArtifactPanel ? "px-3 pt-3 sm:px-4 sm:pt-4" : "px-4 pt-4",
          isDragOver && "border-2 border-dashed border-primary"
        )}
        onDragOver={(e) => e.preventDefault()}
        onDragEnter={(e) => {
          e.preventDefault();
          setIsDragOver(true);
        }}
        onDragLeave={(e) => {
          e.preventDefault();
          setIsDragOver(false);
        }}
        onDrop={handleDrop}
      >
        {isDragOver && (
          <div className="absolute inset-0 z-10 flex items-center justify-center bg-accent/60 rounded-xl pointer-events-none">
            <span className="text-primary font-semibold">Drop CSV file here</span>
          </div>
        )}
        <textarea
          value={input}
          onChange={(e) => setInput(e.target.value)}
          placeholder="Ask a question..."
          disabled={sending || loadingHistory}
          rows={3}
          onKeyDown={(e) => {
            if (e.key === "Enter" && !e.shiftKey) {
              e.preventDefault();
              handleSend();
            }
          }}
          className="w-full resize-none overflow-y-auto rounded-lg border-0 bg-transparent p-0 text-sm text-foreground placeholder:text-muted-foreground focus:outline-none focus:ring-0 disabled:cursor-not-allowed disabled:opacity-50"
        />
        <div className="flex items-center justify-between">
          <ChatControls
            ref={chatControlsRef}
            conversationId={conversationId}
            onChatCleared={() => {
              setMessages([
                {
                  text: "Hi! Ask me anything about your marketing performance.",
                  isUser: false,
                },
              ]);
              setActiveArtifact(null);
            }}
            disabled={loadingHistory || sending}
          />
          <Button
            onClick={handleSend}
            disabled={sending || loadingHistory}
            size="icon"
            className="shrink-0 h-7 w-7 rounded-md"
          >
            <Send className="h-4 w-4" />
          </Button>
        </div>
      </div>
    </div>
  );

  return (
    <div className="relative flex h-full min-h-0 flex-1 overflow-hidden">
      {isMdUp ? (
        <PanelGroup
          id="chat-main-split"
          direction="horizontal"
          className="flex h-full min-h-0 min-w-0 w-full flex-1"
        >
          <Panel
            id="chat"
            minSize={showArtifactPanel ? 25 : 100}
            defaultSize={showArtifactPanel ? 50 : 100}
            style={{ overflow: "hidden" }}
            className="flex min-h-0 min-w-0 flex-col"
          >
            <div className="flex h-full min-h-0 flex-col overflow-hidden">{chatColumn}</div>
          </Panel>
          {showArtifactPanel ? (
            <>
              <PanelResizeHandle
                id="artifact-split"
                className={cn(
                  "relative w-2 bg-border outline-none transition-colors",
                  "hover:bg-violet-500/35 focus-visible:bg-violet-500/45"
                )}
              />
              <Panel
                id="artifact"
                defaultSize={50}
                minSize={25}
                style={{ overflow: "hidden" }}
                className="flex min-h-0 min-w-0 flex-col"
              >
                <ChatArtifactPane
                  activeArtifact={activeArtifact}
                  onOpenPdfAttachment={(pdfAttachment) =>
                    setActiveArtifact({ kind: "pdf", attachment: pdfAttachment })
                  }
                />
              </Panel>
            </>
          ) : null}
        </PanelGroup>
      ) : (
        <>
          <div className="flex min-h-0 min-w-0 flex-1 flex-col overflow-hidden">{chatColumn}</div>
          {showArtifactPanel ? (
            <div className="absolute inset-0 z-20 flex min-h-0 flex-col bg-background shadow-xl">
              <ChatArtifactPane
                activeArtifact={activeArtifact}
                overlayMode
                onClose={() => setActiveArtifact(null)}
                onOpenPdfAttachment={(pdfAttachment) =>
                  setActiveArtifact({ kind: "pdf", attachment: pdfAttachment })
                }
              />
            </div>
          ) : null}
        </>
      )}
    </div>
  );
}
